import { useMemo, useState } from "react";
import {
  Button,
  ButtonVariant,
  Hide,
  Input,
  Radio,
  Show
} from "shared/components";
import {
  Applicant,
  BankAccount,
  CheckoutSubmissionType,
  SubmissionType
} from "shared/types";
import { Card } from "shared/components/Card";
import {
  CreditCardIcon,
  Mail02Icon,
  User03Icon
} from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { ApplicantRoleLabel } from "shared/utils/applicant-role-label";
import { formatToCurrency } from "shared/utils";
import FeeDisplay from "shared/components/FeeDisplay";
import { TemplateCosts } from "../../../types";

interface CheckoutOptionsSelectorProps {
  applicant: Applicant;
  templateCosts?: TemplateCosts;
  propertyBankAccount?: BankAccount;
  updatePreferredCheckoutType: (data: {
    preferred_checkout_type: CheckoutSubmissionType;
    email?: string;
  }) => Promise<unknown>;
  isUpdatingPreferredCheckoutType: boolean;
}

interface CheckoutOptionData {
  id: string;
  type: CheckoutSubmissionType;
  icon: IconSvgElement;
  title: string;
  description: string;
}

interface CheckoutOptionProps {
  option: CheckoutOptionData;
  isSelected: boolean;
  onSelect: (type: CheckoutSubmissionType) => void;
}

const checkoutOptions: CheckoutOptionData[] = [
  {
    id: "checkout-type-by-email",
    type: SubmissionType.SendByEmail,
    icon: Mail02Icon,
    title: "Send payment request to applicant",
    description: "Send out a payment request via email to the applicant"
  },
  {
    id: "checkout-type-in-person",
    type: SubmissionType.InPerson,
    icon: CreditCardIcon,
    title: "Pay here by applicant's card",
    description: "Enter applicant's card details to pay application fee"
  },
  {
    id: "checkout-type-paid-in-cash",
    type: SubmissionType.CheckoutPaidInCash,
    icon: User03Icon,
    title: "Applicant has or will pay in-person",
    description:
      "Screening cost will be charged to the property's linked bank account"
  }
];

const CheckoutOption = ({
  option,
  isSelected,
  onSelect
}: CheckoutOptionProps) => (
  <Radio
    id={option.id}
    name="checkout-type"
    className="p-4"
    label={
      <div className="flex gap-4 items-center">
        <HugeiconsIcon
          icon={option.icon}
          size={20}
          className={isSelected ? "text-primary-500" : "text-alpha-100"}
        />
        <div>
          <p className="text-sm font-medium text-content-1000 mb-1">
            {option.title}
          </p>
          <p className="text-content-300 text-xs">{option.description}</p>
        </div>
      </div>
    }
    checked={isSelected}
    onChange={() => onSelect(option.type)}
  />
);

export const CheckoutOptionsSelector = ({
  applicant,
  templateCosts,
  propertyBankAccount,
  updatePreferredCheckoutType,
  isUpdatingPreferredCheckoutType
}: CheckoutOptionsSelectorProps) => {
  const [checkoutConfig, setCheckoutConfig] = useState<{
    preferred_checkout_type: CheckoutSubmissionType;
    email: string;
  }>({
    preferred_checkout_type: SubmissionType.SendByEmail,
    email: applicant.manual_email || ""
  });

  const totalCost = useMemo(() => {
    if (
      checkoutConfig.preferred_checkout_type ===
      SubmissionType.CheckoutPaidInCash
    )
      return {
        cents: -(templateCosts?.screening_fee?.cents ?? 0),
        currency: templateCosts?.screening_fee?.currency
      };

    return templateCosts?.partners_profit || templateCosts?.partners_fee;
  }, [checkoutConfig.preferred_checkout_type, templateCosts]);

  return (
    <>
      <div className="h-[calc(80vh-154px)] overflow-y-auto p-6">
        <div className="space-y-3 max-w-[460px] mx-auto">
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-content-300">
                Screening cost
              </p>
              <p className="text-sm font-medium">
                {templateCosts?.screening_fee
                  ? formatToCurrency(templateCosts.screening_fee)
                  : "--"}
              </p>
            </div>
          </Card>

          <Card className="p-5 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-content-300">
                Application fee
              </p>
              <p className="text-sm font-medium">
                {applicant.application_fee
                  ? formatToCurrency(applicant.application_fee)
                  : "Free"}
              </p>
            </div>

            <div className="flex items-center justify-between space-y-2">
              <p className="text-sm font-medium text-content-300">Paid by</p>
              <p className="text-sm font-medium">
                {ApplicantRoleLabel[applicant.role || "applicant"]}
              </p>
            </div>
          </Card>

          <Hide if={applicant.application_fee?.cents === 0}>
            <Card className="divide-y-[1px] divide-alpha-40">
              {checkoutOptions.map((option) => (
                <CheckoutOption
                  key={option.id}
                  option={option}
                  isSelected={
                    checkoutConfig.preferred_checkout_type === option.type
                  }
                  onSelect={(type) =>
                    setCheckoutConfig({
                      ...checkoutConfig,
                      preferred_checkout_type: type
                    })
                  }
                />
              ))}
            </Card>
          </Hide>

          <Show
            if={
              checkoutConfig.preferred_checkout_type ===
              SubmissionType.SendByEmail
            }
          >
            <Card className="p-5">
              <div className="animate-appear space-y-4">
                <div className="space-y-1">
                  <h2 className="text-sm font-medium">Please add email</h2>
                  <p className="text-sm text-content-400 font-medium mb-4">
                    Applicant will receive checkout request via email
                  </p>
                </div>
                <Input
                  error={!checkoutConfig.email.length}
                  id="applicant-email"
                  className="w-[300px] !mb-0"
                  placeholder="johndoe@example.com"
                  hideOptional
                  type="email"
                  value={checkoutConfig.email}
                  onChange={(e) => {
                    setCheckoutConfig({
                      ...checkoutConfig,
                      email: e.target.value
                    });
                  }}
                />
              </div>
            </Card>
          </Show>

          <Card className="p-5">
            <div className="space-y-2 pb-4 mb-4 border-b border-alpha-100">
              <Show
                if={
                  checkoutConfig.preferred_checkout_type !==
                  SubmissionType.CheckoutPaidInCash
                }
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-content-300">
                    Credit card processing fee (3.5%)
                  </p>
                  <p className="text-sm font-medium">
                    {templateCosts?.credit_card_processing_fee
                      ? "-" +
                        formatToCurrency(
                          templateCosts.credit_card_processing_fee
                        )
                      : "--"}
                  </p>
                </div>
              </Show>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-content-300">Total</p>
                <FeeDisplay
                  feeInCents={totalCost?.cents}
                  currency={totalCost?.currency}
                  className="text-sm font-bold"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-content-300">
                Will be added to the property&apos;s linked bank account
              </p>
              <p className="text-sm font-medium">
                {propertyBankAccount?.account_number ?? "--"}
              </p>
            </div>
          </Card>
        </div>
      </div>
      <div className="bg-white mt-auto py-4 px-6 flex justify-end border-t border-alpha-100 gap-2">
        <Button
          tooltipZIndex={51}
          variant={ButtonVariant.PRIMARY}
          className="w-[125px]"
          disabled={
            checkoutConfig.preferred_checkout_type ===
              SubmissionType.SendByEmail && !checkoutConfig.email.length
          }
          tooltip={
            checkoutConfig.preferred_checkout_type ===
              SubmissionType.SendByEmail && !checkoutConfig.email.length
              ? "Please enter applicant email"
              : undefined
          }
          loading={isUpdatingPreferredCheckoutType}
          onClick={() => {
            updatePreferredCheckoutType({
              preferred_checkout_type: checkoutConfig.preferred_checkout_type,
              email: checkoutConfig.email
            });
          }}
        >
          Continue
        </Button>
      </div>
    </>
  );
};
